<template>
	<div v-if="listOfAnchors.length" ref="sentinelRef" class="h-0 !m-0" aria-hidden="true" />
	<section
		v-if="listOfAnchors.length"
		id="sticky-anchor-nav"
		ref="stickyRef"
		class="sticky top-[var(--offset-top)] z-30 pt-2 sm:pb-2 transition-all duration-100"
		:class="{'bg-background-secondary': isSticky, '!my-6 md:!my-8' : setVerticalSpacing}"
		:style="{'--offset-top': `${headerHeight}px`}"
		tabindex="0"
	>
		<nav
			class="relative md:container"
			:class="{'flex justify-center': centerNav && !isSticky}"
			aria-label="Navigace v rámci stránky"
		>
			<HorizontalScroll ref="horizontalScrollRef" role="tablist" class="px-4 md:px-0 flex gap-2" fade-edges>
				<li v-for="link in listOfAnchors" :key="link._uid" role="tab" class="inline-flex">
					<UiTabToggleButton
						v-if="link.displayInMenu"
						:key="link._uid"
						:as="AbsoluteInternalLink"
						:href="`#${link.anchor}`"
						:aria-controls="link.anchor"
						:aria-selected="activeAnchor === link.anchor"
						:active="activeAnchor === link.anchor"
						:sticky="isSticky"
						@click.prevent="handleClick(link)"
					>
						{{ link.label }}
					</UiTabToggleButton>
				</li>
			</HorizontalScroll>
		</nav>
	</section>
</template>

<script setup lang="ts">
	import type {AnchorStoryblok} from '@/storyblok/types';
	import type {ComponentObject} from '@/types/storyblok';
	import type {CustomAnchor} from '@/types/anchorNav';
	import {HEADER_DEFAULT_HEIGHT} from '#shared/constants';

	interface Props {
		blok?: ComponentObject | Record<string, any>;
		list?: string[];
		centerNav?: boolean;
		setVerticalSpacing?: boolean;
		customAnchors?: CustomAnchor[];
	}

	type Emit = {
		(e: 'tabClick', tab: AnchorStoryblok | CustomAnchor): void
	};

	const props = defineProps<Props>();
	const emit = defineEmits<Emit>();
	const route = useRoute();

	const stickyRef = useTemplateRef<HTMLElement | null>('stickyRef');
	const sentinelRef = useTemplateRef<HTMLElement | null>('sentinelRef');
	const headerRef = ref<HTMLElement | null>(null);

	const isSticky = ref<boolean>(false);
	const {height: headerHeight} = useElementBounding(headerRef);
	const {height: stickyNavHeight} = useElementBounding(stickyRef);
	const {y: scrollY} = useWindowScroll();
	const {height: windowHeight} = useWindowSize();
	const isMounted = useMounted();

	const sentinelOffsetTop = computed(() => {
		if (!sentinelRef.value) {
			return 0;
		}
		let el: HTMLElement | null = sentinelRef.value;
		let top = 0;
		while (el) {
			top += el.offsetTop;
			el = el.offsetParent as HTMLElement | null;
		}
		return top;
	});

	const AbsoluteInternalLink = resolveComponent('AbsoluteInternalLink');

	const listOfAnchors = computed(() => {
		const custom = (props.customAnchors || []).map((item, index) => ({
			_uid: item._uid || `custom-anchor-${index}-${item.anchor}`,
			anchor: item.anchor,
			label: item.label,
			displayInMenu: item.displayInMenu ?? true,
		}));

		const found = (props.blok && props.list?.length)
			? findAnchorComponents(props.blok, props.list)
			: [];

		return [...custom, ...found];
	});

	const anchorElements = ref<Map<string, HTMLElement>>(new Map());
	const elementVisibilities = reactive<Record<string, boolean>>({});
	const visibilityStopHandles = ref<Array<() => void>>([]);

	const setupAnchorObservers = () => {
		visibilityStopHandles.value.forEach((stop) => stop());
		visibilityStopHandles.value = [];

		anchorElements.value.clear();
		Object.keys(elementVisibilities).forEach((key) => {
			delete elementVisibilities[key];
		});

		nextTick(() => {
			listOfAnchors.value.forEach((anchor) => {
				const anchorElement = document.getElementById(anchor.anchor);
				const contentElement = anchorElement?.nextElementSibling as HTMLElement | null;

				if (contentElement) {
					anchorElements.value.set(anchor.anchor, contentElement);

					const contentRef = ref(contentElement);
					const isVisible = useElementVisibility(contentRef, {threshold: 0.1});

					const stopWatch = watch(
						isVisible,
						(visible) => {
							elementVisibilities[anchor.anchor] = visible;
						},
						{immediate: true},
					);

					visibilityStopHandles.value.push(stopWatch);
				}
			});
		});
	};

	const activeAnchor = computed(() => {
		if (!isMounted.value) {
			return '';
		}

		unref(scrollY);
		const headerOffset = headerHeight.value || HEADER_DEFAULT_HEIGHT;
		const stickyOffset = stickyNavHeight.value || 0;
		const totalOffset = isSticky.value ? headerOffset + stickyOffset : headerOffset;
		const viewportHeight = windowHeight.value;

		let bestAnchor = '';
		let maxVisibleArea = 0;

		Object.entries(elementVisibilities).forEach(([anchorId, isVisible]) => {
			if (isVisible) {
				const element = anchorElements.value.get(anchorId);
				if (element) {
					const rect = element.getBoundingClientRect();

					const visibleTop = Math.max(rect.top, totalOffset);
					const visibleBottom = Math.min(rect.bottom, viewportHeight);
					const visibleHeight = Math.max(0, visibleBottom - visibleTop);
					const visibleArea = visibleHeight * rect.width;

					if (visibleArea > maxVisibleArea) {
						maxVisibleArea = visibleArea;
						bestAnchor = anchorId;
					}
				}
			}
		});

		return bestAnchor;
	});

	const updateIsSticky = () => {
		const offsetTop = headerHeight.value || HEADER_DEFAULT_HEIGHT;

		if (scrollY.value <= 0 || sentinelOffsetTop.value === 0) {
			isSticky.value = false;
			return;
		}

		isSticky.value = scrollY.value >= sentinelOffsetTop.value - offsetTop;
	};

	watchThrottled([scrollY, headerHeight, sentinelOffsetTop], updateIsSticky, {throttle: 100, immediate: true});

	onMounted(() => {
		headerRef.value = document.querySelector('#header');
		setupAnchorObservers();
	});

	watch(
		() => route.fullPath,
		() => {
			setupAnchorObservers();
		},
	);

	onBeforeUnmount(() => {
		visibilityStopHandles.value.forEach((stop) => stop());
		visibilityStopHandles.value = [];
	});

	function handleClick(link: AnchorStoryblok | CustomAnchor | {_uid: string; anchor: string; label: string; displayInMenu: boolean}) {
		navigateTo({hash: `#${link.anchor}`});
		emit('tabClick', link);
	}
</script>
